//@ts-ignore
import Mazemap from "@itpi/mazemap";
import { useQuery } from "@tanstack/react-query";
import { Point, Geometry, FeatureCollection } from "geojson";
import { useContext, useEffect, useState } from "react";
import { MapContext } from "../context/map.context";

export type LocationSearchResult =
  | {
      type: "poi";
      zValue: number;
      zName: string;
      bldId: number;
      dispBldNames: string[];
      dispPoiNames: string[];
      dispTypeNames: string[];
      typeIds: number[];
      title: string;
      campusId: number;
      identifier: string;
      poiId: number;
      point: Point;
    }
  | {
      type: "category";
      title: string;
      id: number;
      dispTypeNames: string[];
    }
  | {
      campusId: number;
      dispBldNames: string[];
      id: number;
      point: Point;
      title: string;
      type: "building";
    };

export type Poi = {
  properties: {
    title: string;
    id: number;
    poiId: number;
    identifier?: any;
    campusId: number;
    floorId: number;
    zLevel: number;
    floorName: string;
    buildingName: string;
    infoUrl?: any;
    infoUrlText?: any;
    description: string | null;
    images: any[];
    names: string[];
    types: {
      poiTypeId: number;
      name: string;
      iconId?: any;
    }[];
    externalReferenceTypes: any[];
  };
  geometry: Geometry;
  type: "Feature";
};

export const useMapSearch = (query: string) => {
  const { location } = useContext(MapContext);

  return useQuery<LocationSearchResult[]>(
    ["Search", query, location],
    async () => {
      const searchController = new Mazemap.Search.SearchController({
        campusid: 341,
        rows: 50,
      });

      searchController.options.lng = location?.lng;
      searchController.options.lat = location?.lat;
      searchController.options.boostbydistance = true;
      searchController.options.searchdiameter = 10;

      const data: { results: LocationSearchResult[] } =
        await searchController.search(query);
      return data.results;
    },
    {
      enabled: query.length > 0,
    }
  );
};

export const useBuilding = (id: number) => {
  return useQuery(["GetBuilding", id], () => Mazemap.Data.getBuilding(id));
};

export const usePoi = (id?: number) => {
  return useQuery(["GetPoi", id], () => Mazemap.Data.getPoi(id), {
    enabled: !!id,
  });
};

export const useCategory = (id: number) => {
  return useQuery(["GetCategory", id], async () => {
    const category = await Mazemap.Data.getTypeById(id);
    const icon = category.icon_id
      ? Mazemap.Data.getIconSvgUrl(category.icon_id)
      : null;
    return { ...category, icon };
  });
};

// Fetches a category by ID and includes its icon URL if available
const fetchCategoryById = async (id: number) => {
  const category = await Mazemap.Data.getTypeById(id);
  const icon = category.icon_id
    ? Mazemap.Data.getIconSvgUrl(category.icon_id)
    : null;
  return { ...category, icon };
};

// Custom hook to fetch categories (POI Types) by IDs
export const useAllCategories = (ids: number[]) => {
  return useQuery(
    ["GetAllCategories", ids],
    async () => {
      const categories = await Promise.all(ids.map(fetchCategoryById));
      return categories;
    },
    {
      enabled: ids.length > 0, // Only run the query if there are IDs
    }
  );
};

//used by mapsearch to get the poi IDs
export const usePoiTypes = () => {
  return useQuery(["GetPoiTypes"], async () => {
    try {
      const data = await Mazemap.Data.getSelectablePoiTypesForCampusId(341);
      return data;
    } catch (error) {
      console.error("Error fetching POI types:", error);
      throw error;
    }
  });
};

// For the category search results
export const useCategoryPois = (id: number) => {
  return useQuery<FeatureCollection>(["GetCategoryPois", id], () =>
    Mazemap.Data.getPoisByCategoryAndCampusId(id, 341)
  );
};

export type RoutePoint = {
  lngLat: { lng: number; lat: number };
  zLevel: number;
};

export const useRoute = (from?: RoutePoint, to?: RoutePoint) => {
  const { map, routeController, fitBounds } = useContext(MapContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isInstructionsLoading, setIsInstructionsLoading] = useState(false);
  const [isInstructionsError, setIsInstructionsError] = useState(false);
  const [data, setData] = useState<Geometry>();
  const [instructionsData, setInstructionsData] = useState<any>()

  useEffect(() => {
    if (from && to && routeController && map) {
      setIsLoading(true);
      Mazemap.Data.getRouteJSON(from, to, { mode: "PEDESTRIAN" })
        .then((geojson: Geometry) => {
          setData(geojson);
          setIsLoading(false);
          routeController.setPath(geojson);
          const bounds = Mazemap.Util.Turf.bbox(geojson);
          fitBounds(bounds, {
            padding: { left: 100, top: 100, right: 100, bottom: 300 },
            bearing: 0,
            pitch: 0,
          });
        })
        .catch(() => {
          setIsLoading(false);
          setIsError(true);
        });
      return () => routeController.clear();
    }
  }, [from, to, map, routeController, fitBounds]);

  useEffect(() => {
    if (from && to && map) {
      setIsInstructionsLoading(true);
      Mazemap.Data.getDirections(from, to, { mode: "PEDESTRIAN" })
        .then((geojson: any) => {
          console.log(77, geojson)
          setInstructionsData(geojson);
          setIsInstructionsLoading(false);
        })
        .catch(() => {
          setIsInstructionsLoading(false);
          setIsInstructionsError(true);
        });
    }
  }, [from, to, map, fitBounds]);

  return { data, isLoading, isError, instructionsData, isInstructionsLoading, isInstructionsError };
};
